/**
 * Phase 4 — Offer portal (G.12/G.13), plus the offer record (Lloyd, Aug 18).
 *
 * The draft endpoints build message text from whatever is typed at the time and keep nothing. The
 * record endpoints below store the offer, which is what the "Offer received" box expands into and
 * what the send is built from.
 */
export type OfferType = 'dsc' | 'deferral' | 'conviction' | 'appeal';

/** How the offer reached the firm. Lloyd's four routes, plus a catch-all. */
export type OfferSource = 'pretrial_notice' | 'court_email' | 'phone' | 'hearing' | 'other';

export interface DraftedEmail {
  subject: string;
  body: string;
  learnMoreUrl: string;
}

export interface OfferRecord {
  guid: string;
  offerType: OfferType;
  source: OfferSource;
  deferralDays: number | null;
  deferralFeeUsd: number | null;
  dscAlsoRequired: boolean;
  notes: string | null;
  receivedAt: string;
  conveyedAt: string | null;
  conveyedChannels: string | null;
  clientResponse: 'accepted' | 'rejected' | null;
  respondedAt: string | null;
  recordedAt: string;
}

export interface RecordOfferInput {
  offerType: OfferType;
  source: OfferSource;
  deferralDays?: number | null;
  deferralFeeUsd?: number | null;
  dscAlsoRequired?: boolean;
  notes?: string | null;
  receivedAt?: string | null;
}

export interface OfferPreview {
  email: DraftedEmail;
  smsText: string;
  recipientEmail: string | null;
  recipientPhone: string | null;
}

export interface OfferSendResult {
  offer: OfferRecord;
  emailSent: boolean;
  smsSent: boolean;
  /** Channels that could not be used, phrased for staff. Empty when both went out. */
  problems: string[];
}

function host(): string {
  return process.env.API_HOST?.trim() || window.location.origin;
}

/**
 * The server explains refusals in the body — "no email address on file", "already been sent". Those
 * sentences are the useful half of the failure, so they are surfaced rather than replaced with a
 * generic message.
 */
async function readError(response: Response, fallback: string): Promise<Error> {
  try {
    const body = await response.json();
    return new Error(body?.message || body?.error || fallback);
  } catch {
    return new Error(fallback);
  }
}

class OfferPortalApi {
  /** G.12 — draft the client offer email (Lloyd's wording + Ken's learn-more link). */
  static async buildOfferEmail(
    ticketGuid: string,
    offerType: OfferType,
    opts?: { deferralDays?: number | null; deferralFeeUsd?: number | null; dscAlsoRequired?: boolean },
  ): Promise<DraftedEmail> {
    const response = await fetch(`${host()}/api/v1/manager/ticket/${ticketGuid}/offer-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ offerType, ...opts }),
    });
    if (!response.ok) throw await readError(response, 'Failed to build offer email');
    return response.json();
  }

  /** G.13 — draft the court acceptance for staff to review/send. */
  static async buildCourtAcceptance(ticketGuid: string, offerType: OfferType): Promise<DraftedEmail> {
    const response = await fetch(`${host()}/api/v1/manager/ticket/${ticketGuid}/offer-acceptance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ offerType }),
    });
    if (!response.ok) throw await readError(response, 'Failed to build court acceptance draft');
    return response.json();
  }

  /** Every offer on the case, newest first — an appeal characteristically produces a second one. */
  static async listOffers(ticketGuid: string): Promise<OfferRecord[]> {
    const response = await fetch(`${host()}/api/v1/manager/ticket/${ticketGuid}/offers`, {
      credentials: 'include',
    });
    if (!response.ok) throw await readError(response, 'Failed to load the offers on this case');
    const body = await response.json();
    return body.offers ?? [];
  }

  /** Manual entry — the primary path for offers made over the phone or at a hearing. */
  static async recordOffer(ticketGuid: string, input: RecordOfferInput): Promise<OfferRecord> {
    const response = await fetch(`${host()}/api/v1/manager/ticket/${ticketGuid}/offer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });
    if (!response.ok) throw await readError(response, 'Failed to record the offer');
    return response.json();
  }

  /** Exactly what the client will receive, built server-side from the saved record. */
  static async previewOffer(offerGuid: string): Promise<OfferPreview> {
    const response = await fetch(`${host()}/api/v1/manager/offer/${offerGuid}/preview`, {
      credentials: 'include',
    });
    if (!response.ok) throw await readError(response, 'Failed to build the offer preview');
    return response.json();
  }

  /** Send it. `resend` is required to convey an offer that has already gone out once. */
  static async sendOffer(offerGuid: string, resend = false): Promise<OfferSendResult> {
    const response = await fetch(`${host()}/api/v1/manager/offer/${offerGuid}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ resend }),
    });
    if (!response.ok) throw await readError(response, 'Failed to send the offer');
    return response.json();
  }
}

export default OfferPortalApi;
